<!--
  @fileoverview GenAI Citation Modal Component (Vue 3)

  Modal dialog that displays detailed information about a citation
  source from GenAI responses, including:
  - Source title
  - Snippet/quote from the document
  - Confidence/relevance score
  - Link to view full source

  @component GenAICitationModal
  @displayName Source Details Modal

  @example
  <GenAICitationModal
    :visible="showModal"
    :citation="citationData"
    :showFullSource="true"
    @closeModal="handleClose"
  />
-->
<template>
  <el-dialog
    v-model="localVisible"
    width="500px"
    top="10vh"
    :modal="false"
    :append-to-body="true"
    @close="emit('closeModal')"
    class="p-[10px] keyreply-citation-modal"
  >
    <template #header>Source Details</template>
    <div class="keyreply-source-content">
      <h3 class="keyreply-source-title">{{ citation.source }}</h3>

      <el-card class="keyreply-quote-card" shadow="never">
        <blockquote class="keyreply-quote">
          {{ shouldShowFullQuote ? citation.snippet : blockQuote }}
          <!-- Read more -->
          <el-button
            type="primary"
            link
            @click="shouldShowFullQuote = !shouldShowFullQuote"
            v-if="citation.snippet.length > 200"
          >
            {{ shouldShowFullQuote ? "Read Less" : "Read More" }}
          </el-button>
        </blockquote>
      </el-card>

      <div class="keyreply-confidence-section">
        <span>Confidence:</span>
        <el-progress :percentage="citationConfidence" class="flex-1 mx-[10px] my-0" />
      </div>

      <el-button
        v-if="showFullSource"
        type="primary"
        class="keyreply-view-button"
        @click="onViewSource"
        plain
      >
        <el-icon class="mr-1"><Link /></el-icon>
        View Full Source
      </el-button>
    </div>
  </el-dialog>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/GenAICitationModal
 * @description Modal for displaying citation source details (Vue 3 Composition API)
 */
import { ref, computed } from "vue";
import { Link } from "@element-plus/icons-vue";
import dayjs from "dayjs";

/**
 * Component props
 */
const props = defineProps({
  /**
   * Controls modal visibility
   */
  visible: {
    type: Boolean,
    default: false,
  },
  /**
   * Citation object with source details
   */
  citation: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Whether to show the "View Full Source" button
   */
  showFullSource: {
    type: Boolean,
    default: false,
  },
});

/**
 * Component emits
 */
const emit = defineEmits(["closeModal", "update:visible"]);

/**
 * Local dialog visibility computed for v-model binding
 * Syncs with parent prop and emits closeModal on dialog close
 */
const localVisible = computed({
  get: () => props.visible,
  set: (val) => {
    if (!val) {
      emit("closeModal");
      emit("update:visible", val);
    }
  },
});

/**
 * Local state
 */
const shouldShowFullQuote = ref(false);

/**
 * Open the full source document in a new tab
 */
function onViewSource() {
  window.open(props.citation.documentUrl, "_blank");
}

/**
 * Computed citation confidence percentage
 * Converts relevance score (0-1) to percentage
 */
const citationConfidence = computed(() => {
  return parseFloat((props.citation.relevance * 100).toFixed(1));
});

/**
 * Computed year from upload timestamp
 */
const updatedYear = computed(() => {
  return dayjs(props.citation.uploadedAt).format("YYYY");
});

/**
 * Computed block quote with truncation
 * Shows first ~200 characters worth of words
 */
const blockQuote = computed(() => {
  // Cut into 200 characters
  const words = props.citation.snippet.split(" ");
  let quote = "";
  let wordCount = 0;
  if (shouldShowFullQuote.value) {
    return props.citation.snippet;
  }
  for (let i = 0; i < words.length; i++) {
    if (wordCount >= 200) {
      break;
    }
    quote += words[i] + " ";
    wordCount += words[i].length;
  }
  return quote;
});
</script>

<style scoped>
.keyreply-citation-modal {
  font-family: "Montserrat", sans-serif;
}
.keyreply-source-title {
  margin-bottom: 10px;
  font-weight: bold;
  font-size: 18px;
}

.keyreply-source-meta {
  color: #777;
  font-size: 16px;
  margin-bottom: 16px;
}

.keyreply-quote-card {
  background: #f9fafb;
  border-left: 4px solid #409eff;
  margin-bottom: 20px;
}

.keyreply-quote {
  color: #333;
  margin: 0;
  font-size: 16px;
}

.keyreply-confidence-section {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.keyreply-confidence-percent {
  color: #333;
}

.keyreply-view-button {
  width: 100%;
  background-color: rgb(78, 108, 206);
  color: #fff;
  border-color: rgb(78, 108, 206);
}

.keyreply-view-button:hover {
  background-color: rgb(78, 108, 206);
  color: #fff;
  border-color: rgb(78, 108, 206);
}
</style>

<style>
.keyreply-citation-modal .el-dialog__body {
  padding: 10px 20px !important;
}
</style>
